'use es6';

import * as ActionTypes from './ActionTypes';
import * as ecosystemClient from '../clients/ecosystemClient';
import Immutable from 'immutable';
import ListingView from '../records/ListingView';
import ThinListing from 'ecosystem-marketplace-core/records/ThinListing';
import { maybeEarlyRequest } from '../utils/earlyRequests';
import { replace } from '../utils/history';
import { getCategorySuperCategory } from 'ecosystem-marketplace-core/utils/groupTags';
import { parse as parseParams } from 'hub-http/helpers/params';
import * as CategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/SuperCategorySlug';
import { getIsPublic, getLangCode } from '../selectors/auth';

const validateDetailsUrl = (category, path, search, slug, isAdminPreview, isCustomer, activeCategory) => {
  const {
    published
  } = parseParams(search.slice(1));
  const isPreview = published === 'false' || path === '/_preview/:slug' || isAdminPreview;
  const isShortlink = path.includes('/_detail/:slug');
  const isReviewLink = path.includes('/:slug/write-review');

  if (!isCustomer && isPreview && !isAdminPreview) {
    const publicPreviewError = new Error('Forbidden public preview');
    publicPreviewError.status = 403;
    throw publicPreviewError;
  }

  if (isShortlink) {
    const superCategory = category ? getCategorySuperCategory(category) : null;

    if (!(category && superCategory)) {
      const redirectError = new Error('Super category & category required to redirect');
      redirectError.status = 404;
      throw redirectError;
    }

    const superCategoryFacet = SuperCategorySlug[superCategory];
    const categoryFacet = CategorySlug[category];
    replace(`/${superCategoryFacet}/${categoryFacet}/${slug}${isReviewLink ? '/write-review' : ''}${search}`);
  }

  if (!isPreview && activeCategory && category !== activeCategory) {
    const categoryError = new Error('Invalid category slug');
    categoryError.status = 404;
    throw categoryError;
  }
};

export const fetchAppReviewability = (offeringId, slug) => dispatch => {
  const meta = {
    slug
  };
  dispatch({
    type: ActionTypes.DETAILS_REVIEWABILITY_REQUESTED,
    meta
  });
  ecosystemClient.fetchReviewability(offeringId).then(resp => {
    dispatch({
      type: ActionTypes.DETAILS_REVIEWABILITY_RECEIVED,
      payload: resp,
      meta
    });
  }).catch(err => {
    dispatch({
      type: ActionTypes.DETAILS_REVIEWABILITY_FAILED,
      payload: err,
      meta
    });
  });
};
export const fetchDetails = (slug, isPublished, isAdminPreview, {
  path,
  search,
  isCustomer,
  activeCategory,
  languageQuery
}) => (dispatch, getState) => {
  const meta = {
    slug
  };
  const state = getState();
  const langCode = getLangCode(state);
  dispatch({
    type: ActionTypes.DETAILS_FETCH_REQUESTED,
    meta
  });
  let request;

  if (isAdminPreview) {
    request = ecosystemClient.detailsAdminPreview(slug, languageQuery);
  } else if (!isPublished) {
    request = maybeEarlyRequest(`details:preview:${slug}`, () => ecosystemClient.detailsPreview(slug, languageQuery));
  } else {
    request = maybeEarlyRequest(`details:${slug}`, () => ecosystemClient.details(slug, langCode));
  }

  return request.then(result => {
    const newListingView = new ListingView(result);
    const category = newListingView.category;
    validateDetailsUrl(category, path, search, slug, isAdminPreview, isCustomer, activeCategory);
    dispatch({
      type: ActionTypes.DETAILS_FETCH_RECEIVED,
      payload: newListingView,
      meta
    });
    const isPublic = getIsPublic(state);

    if (!isPublic && !isAdminPreview) {
      dispatch(fetchAppReviewability(newListingView.get('offeringId'), slug));
    }
  }).catch(error => {
    dispatch({
      type: ActionTypes.DETAILS_FETCH_FAILED,
      payload: error,
      meta
    });
  });
};
export const fetchFeatureCompatibility = () => dispatch => {
  dispatch({
    type: ActionTypes.FEATURE_COMPATIBILITY_REQUESTED
  });
  return ecosystemClient.fetchCompatibility().then(({
    compatibility,
    portalTiers
  }) => {
    dispatch({
      type: ActionTypes.FEATURE_COMPATIBILITY_RECEIVED,
      payload: {
        compatibility: Immutable.fromJS(compatibility),
        portalTiers: new Immutable.Set(portalTiers)
      }
    });
  }).catch(err => {
    dispatch({
      type: ActionTypes.FEATURE_COMPATIBILITY_FAILED,
      payload: err
    });
  });
};
export const fetchMoreApps = (language, moreAppsQueries, slug) => dispatch => {
  dispatch({
    type: ActionTypes.DETAILS_MORE_APPS_FETCH_REQUESTED
  });
  ecosystemClient.searchApps({
    language,
    queries: moreAppsQueries
  }).then(({
    results: [{
      items: _categoryResults
    }, {
      items: _superCategoryResults = []
    } = {}]
  }) => {
    const categoryResults = _categoryResults.filter(({
      slug: _slug
    }) => _slug !== slug);

    const superCategoryResults = _superCategoryResults.filter(({
      slug: _slug
    }) => _slug !== slug);

    const hasCategoryResults = !!categoryResults.length;
    const results = hasCategoryResults ? categoryResults : superCategoryResults;
    dispatch({
      type: ActionTypes.DETAILS_MORE_APPS_FETCH_RECEIVED,
      payload: new Immutable.Map({
        hasCategoryResults,
        results: Immutable.OrderedSet(results.slice(0, 3).map(result => new ThinListing(result)))
      })
    });
  }).catch(__error => {
    dispatch({
      type: ActionTypes.DETAILS_MORE_APPS_FETCH_FAILED
    });
  });
};
export const resetMoreApps = () => dispatch => {
  dispatch({
    type: ActionTypes.DETAILS_MORE_APPS_RESET
  });
};
export const setDetailsReferrer = referrer => dispatch => {
  dispatch({
    type: ActionTypes.DETAILS_REFERRER_SET,
    payload: referrer
  });
};
export const resetDetailsReferrer = () => dispatch => {
  dispatch({
    type: ActionTypes.DETAILS_REFERRER_RESET
  });
};